import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useAccountClaims } from './useAccountClaims';
import { accountsApi } from '../services/accountsApi';
import { canReadSeatsSummary, isSeatsForbidden } from '../rbac/seatsSummary';

export function useSeatsSummaryAccess() {
  const { user } = useAuth();
  const claims = useAccountClaims();
  return (tenantId: string | number | undefined) =>
    !!user &&
    canReadSeatsSummary(
      {
        accountRole: claims.accountRole,
        tenantId: claims.tenantId,
        roles: [user.role, ...(Array.isArray(user.roles) ? user.roles : [])].filter(
          (role): role is string => typeof role === 'string',
        ),
      },
      tenantId,
    );
}

/** 403 é indisponibilidade por permissão; nunca uma quota zero ou sessão expirada. */
export function useSeatsSummary(tenantId: string | undefined, enabled = true) {
  const canRead = useSeatsSummaryAccess()(tenantId);
  const query = useQuery({
    queryKey: ['admin', 'seats-summary', tenantId],
    queryFn: () => {
      // refetch() do React Query ignora enabled: não deixa um retry manual
      // contornar o mesmo gate que protege a montagem automática.
      if (!canRead) throw new Error('Resumo de cadeiras indisponível para este perfil.');
      return accountsApi.getSeatsSummary(tenantId as string);
    },
    enabled: canRead && enabled,
    staleTime: 60_000,
    retry: (count, error) => canRead && !isSeatsForbidden(error) && count < 2,
    throwOnError: false,
  });
  const forbidden = isSeatsForbidden(query.error);
  return { ...query, canRead, forbidden, data: canRead && !forbidden ? query.data : undefined };
}
